import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Stack,
  Button,
  useTheme,
  IconButton,
  Typography,
} from '@mui/material';
import { Product } from '@domain/product/models/Product';
import { Close } from '@mui/icons-material';
import BulkLeadTimeDropdown from '../catalog/BulkLeadTimeDropdown';
import BulkEditNumberDropdown from './BulkEditNumberDropdown';
import BulkEditModeDropdown from './BulkEditModeDropdown';
import { TransportMethod } from '@domain/shipment/models/TransportMethod';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { useProducts } from '../../hooks/useProducts';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import { useToast } from '@shared/hooks/useToast';
import { ProductMode } from '@domain/product/models/ProductMode';
import { ProductFilter } from '@domain/product/models/ProductFilter';

interface InlineBulkEditProps {
  selectedProducts?: Product[];
  products: Product[];
  filter?: ProductFilter;
  onClose: () => void;
}

type LeadTimeFormValues = {
  productionDuration: string;
  processingDuration: string;
  defaultShipmentMethodId?: string;
  selectedShipmentMethod?: ShipmentMethod;
};

const emptyLeadTimeValues = (): LeadTimeFormValues => ({
  productionDuration: '',
  processingDuration: '',
});

const parseDuration = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const BulkEditBar: React.FC<InlineBulkEditProps> = ({
  selectedProducts,
  products,
  filter,
  onClose,
}) => {
  const theme = useTheme();
  const { success, error: showError } = useToast();
  const { bulkEditProducts } = useProducts();
  const { shipmentMethods } = useShipmentMethods();

  const [leadTimeAnchorEl, setLeadTimeAnchorEl] = useState<HTMLElement | null>(null);
  const [orderAmountAnchorEl, setOrderAmountAnchorEl] = useState<HTMLElement | null>(null);
  const [bufferAnchorEl, setBufferAnchorEl] = useState<HTMLElement | null>(null);
  const [modeAnchorEl, setModeAnchorEl] = useState<HTMLElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const productsToEdit = selectedProducts ?? products;
  const filters: ProductFilter | undefined = selectedProducts
    ? { productIds: selectedProducts.map((p) => p.id) }
    : filter;

  const [orderAmountWeeks, setOrderAmountWeeks] = useState<string>('');
  const [bufferWeeks, setBufferWeeks] = useState<string>('');
  const [selectedMode, setSelectedMode] = useState<ProductMode | undefined>(undefined);

  const [leadTimeValues, setLeadTimeValues] = useState<LeadTimeFormValues>(emptyLeadTimeValues);
  const originalLeadTimeValuesRef = useRef<LeadTimeFormValues>(emptyLeadTimeValues());

  const selectionKey = useMemo(
    () => (selectedProducts ?? products).map((p) => p.id).sort().join(','),
    [selectedProducts, products]
  );

  useEffect(() => {
    const toEdit = selectedProducts ?? products;
    if (toEdit.length === 0) return;
    const first = toEdit[0];
    const nextLeadTimeValues = emptyLeadTimeValues();
    setLeadTimeValues(nextLeadTimeValues);
    originalLeadTimeValuesRef.current = nextLeadTimeValues;
    const allSameOrderAmount = toEdit.every((p) => (p.minimumOrderAmountDays ?? 0) === (first.minimumOrderAmountDays ?? 0));
    const allSameBuffer = toEdit.every((p) => (p.minimumDaysOfStockInReserve ?? 0) === (first.minimumDaysOfStockInReserve ?? 0));
    setOrderAmountWeeks(allSameOrderAmount && first.minimumOrderAmountDays != null ? String(first.minimumOrderAmountDays / 7) : '');
    setBufferWeeks(allSameBuffer && first.minimumDaysOfStockInReserve != null ? String(first.minimumDaysOfStockInReserve / 7) : '');
    const allSameMode = toEdit.every((p) => (p.mode ?? '') === (first.mode ?? ''));
    setSelectedMode(allSameMode && first.mode ? first.mode : undefined);
  }, [selectionKey, selectedProducts, products]);

  const resolvedShipmentMethod = useMemo(() => {
    if (leadTimeValues.selectedShipmentMethod) return leadTimeValues.selectedShipmentMethod;
    if (leadTimeValues.defaultShipmentMethodId && shipmentMethods) {
      return shipmentMethods.find((m) => m.id === leadTimeValues.defaultShipmentMethodId);
    }
    return undefined;
  }, [leadTimeValues.selectedShipmentMethod, leadTimeValues.defaultShipmentMethodId, shipmentMethods]);

  const durationToFormValue = (value: unknown) =>
    value === undefined || value === null || value === '' ? '' : String(value);

  const handleLeadTimeValueChange = (field: string, value: unknown) => {
    if (field === 'productionDuration') {
      setLeadTimeValues((prev) => ({ ...prev, productionDuration: durationToFormValue(value) }));
    } else if (field === 'processingDuration') {
      setLeadTimeValues((prev) => ({ ...prev, processingDuration: durationToFormValue(value) }));
    }
  };

  const handleShipmentMethodChange = (method: ShipmentMethod) => {
    setLeadTimeValues((prev) => ({
      ...prev,
      defaultShipmentMethodId: method.id,
      selectedShipmentMethod: method,
    }));
  };

  const handleLeadTimeConfirm = async () => {
    if (productsToEdit.length === 0) return;

    const original = originalLeadTimeValuesRef.current;
    const updateData: Partial<Product> = {};

    if (leadTimeValues.productionDuration !== original.productionDuration) {
      const productionDuration = parseDuration(leadTimeValues.productionDuration);
      if (productionDuration !== undefined) {
        updateData.productionDuration = productionDuration;
      }
    }
    if (leadTimeValues.processingDuration !== original.processingDuration) {
      const processingDuration = parseDuration(leadTimeValues.processingDuration);
      if (processingDuration !== undefined) {
        updateData.processingDuration = processingDuration;
      }
    }
    if (
      leadTimeValues.defaultShipmentMethodId &&
      leadTimeValues.defaultShipmentMethodId !== original.defaultShipmentMethodId
    ) {
      updateData.defaultShipmentMethodId = leadTimeValues.defaultShipmentMethodId;
    }

    if (Object.keys(updateData).length === 0) {
      showError('No changes to save');
      return;
    }

    setIsSubmitting(true);
    try {
      await bulkEditProducts({ updateData, filters });
      success('Lead time updated');
      const resetValues = emptyLeadTimeValues();
      setLeadTimeValues(resetValues);
      originalLeadTimeValuesRef.current = resetValues;
      setLeadTimeAnchorEl(null);
    } catch {
      showError('Failed to update lead time');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOrderAmountConfirm = async () => {
    if (productsToEdit.length === 0) return;
    const weeks = orderAmountWeeks === '' ? undefined : Number(orderAmountWeeks);
    if (weeks !== undefined && (Number.isNaN(weeks) || weeks < 0)) {
      showError('Please enter a valid number');
      return;
    }
    setIsSubmitting(true);
    try {
      await bulkEditProducts({
        updateData: { minimumOrderAmountDays: weeks !== undefined ? Math.round(weeks * 7) : undefined },
        filters,
      });
      success('Order amount updated');
      setOrderAmountAnchorEl(null);
    } catch {
      showError('Failed to update order amount');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBufferConfirm = async () => {
    if (productsToEdit.length === 0) return;
    const weeks = bufferWeeks === '' ? undefined : Number(bufferWeeks);
    if (weeks !== undefined && (Number.isNaN(weeks) || weeks < 0)) {
      showError('Please enter a valid number');
      return;
    }
    setIsSubmitting(true);
    try {
      await bulkEditProducts({
        updateData: { minimumDaysOfStockInReserve: weeks !== undefined ? Math.round(weeks * 7) : undefined },
        filters,
      });
      success('Buffer updated');
      setBufferAnchorEl(null);
    } catch {
      showError('Failed to update buffer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeConfirm = async () => {
    if (productsToEdit.length === 0 || !selectedMode) return;
    setIsSubmitting(true);
    try {
      await bulkEditProducts({
        updateData: { mode: selectedMode },
        filters,
      });
      success('Mode updated');
      setModeAnchorEl(null);
    } catch {
      showError('Failed to update mode');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="body2" color="text.secondary" noWrap>
        Edit {selectedProducts ? selectedProducts.length : 'all'} product{selectedProducts ? (selectedProducts.length !== 1 ? 's' : '') : 's'}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          pointerEvents: 'auto',
          color: 'white',
          backgroundColor: theme.palette.secondary.main,
          p: 0.5,
          borderRadius: 2,
          boxShadow: `0px 0px 16px 0px ${theme.palette.primary.main}33`,
        }}
      >
        <Button variant="text" color="inherit" onClick={(e) => setLeadTimeAnchorEl(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
          Lead Time
        </Button>
        <Button variant="text" color="inherit" onClick={(e) => setOrderAmountAnchorEl(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
          Order amount
        </Button>
        <Button variant="text" color="inherit" onClick={(e) => setBufferAnchorEl(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
          Buffer
        </Button>
        <Button variant="text" color="inherit" onClick={(e) => setModeAnchorEl(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
          Mode
        </Button>
        <IconButton color="inherit" onClick={() => onClose()}>
          <Close />
        </IconButton>
      </Stack>

      <BulkLeadTimeDropdown
        anchorEl={leadTimeAnchorEl}
        open={!!leadTimeAnchorEl}
        onClose={() => setLeadTimeAnchorEl(null)}
        onValueChange={handleLeadTimeValueChange}
        onShipmentMethodChange={handleShipmentMethodChange}
        onConfirm={handleLeadTimeConfirm}
        values={{
          productionDuration: leadTimeValues.productionDuration,
          processingDuration: leadTimeValues.processingDuration,
          transportMethod: TransportMethod.TRUCK,
          shippingDuration: String(resolvedShipmentMethod?.duration ?? 0),
        }}
        selectedShipmentMethod={resolvedShipmentMethod}
        isLoading={isSubmitting}
      />

      <BulkEditNumberDropdown
        anchorEl={orderAmountAnchorEl}
        open={!!orderAmountAnchorEl}
        onClose={() => setOrderAmountAnchorEl(null)}
        title="Order Amount"
        label="Weeks to order"
        value={orderAmountWeeks}
        onChange={setOrderAmountWeeks}
        onConfirm={handleOrderAmountConfirm}
        suffix="weeks"
        placeholder="0"
        isLoading={isSubmitting}
      />

      <BulkEditNumberDropdown
        anchorEl={bufferAnchorEl}
        open={!!bufferAnchorEl}
        onClose={() => setBufferAnchorEl(null)}
        title="Weeks of Buffer"
        label="Weeks of buffer"
        value={bufferWeeks}
        onChange={setBufferWeeks}
        onConfirm={handleBufferConfirm}
        suffix="weeks"
        placeholder="0"
        isLoading={isSubmitting}
      />

      <BulkEditModeDropdown
        anchorEl={modeAnchorEl}
        open={!!modeAnchorEl}
        onClose={() => setModeAnchorEl(null)}
        value={selectedMode}
        onChange={setSelectedMode}
        onConfirm={handleModeConfirm}
        isLoading={isSubmitting}
      />
    </Stack>
  );
};

export default BulkEditBar;
